// Admin curation page for the Licences-side product exclusion list.
// All four roles can read; only Admin can flip
// the per-row exclude toggle / use the bulk actions.

import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AzureAD } from 'react-aad-msal'
import { authProvider } from '../../../services/authProvider'
import store from '../../../store/configureStore'
import {
  Container, Row, Col, Input, Badge, Button,
} from 'reactstrap'
import PageContainer from '../../Shared/LayoutComponents/PageContainer'
import PageHeading from '../../Shared/LayoutComponents/PageHeading'
import LoadingSpinner from '../../Shared/LoadingSpinner'
import {
  fetchLicenseProducts,
  setLicenseProductsExcluded,
} from '../../actions/cspLicensesActions'
import CSPLicenseDevPanel from './CSPLicenseDevPanel'
import { formatLocal } from '../../Shared/formatDateTime'
import { canUseLicenceFeature } from './featureAccess'

const PAGE_SIZE = 50

const formatCurrency = (n) =>
  n == null ? '–' : n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })

// "Per-customer pricing varies" - DD returns a price band per SKU.
// Collapse to a single cell: "$0", "$12.50", or "$10 – $15" range
// depending on whether the values match. Null on both sides shows
// as a dash so absent data is obvious.
const formatPriceRange = (min, max) => {
  if (min == null && max == null) return '–'
  if (min == null) return formatCurrency(max)
  if (max == null) return formatCurrency(min)
  if (min === max) return formatCurrency(min)
  return `${formatCurrency(min)} – ${formatCurrency(max)}`
}

const CSPLicenseProductsInner = ({ isAdmin }) => {
  const dispatch = useDispatch()
  const products = useSelector((s) => s.cspLicenses.products) || { rows: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE }
  const loading = useSelector((s) => s.cspLicenses.productsLoading)
  const mutating = useSelector((s) => s.cspLicenses.productsMutating)

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('all')
  const [pricing, setPricing] = useState('all')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState(new Set())

  const load = useCallback((pageOverride) => {
    dispatch(fetchLicenseProducts({
      search,
      status,
      pricing,
      page: pageOverride || page,
      pageSize: PAGE_SIZE,
    }))
    setSelected(new Set())
  }, [dispatch, search, status, pricing, page])

  // Mount-only effect: kick off the initial fetch. Subsequent loads
  // go through explicit Apply / pagination handlers; the deps array
  // is intentionally narrow.
  useEffect(() => {
    load(1)
    setPage(1)
  }, [])

  // After a mutation finishes (admin clicked Exclude / Include /
  // bulk), reload to show the updated server state. Watches the
  // mutating flag and fires only on its true→false transition.
  useEffect(() => {
    if (!mutating) {
      load()
    }
  }, [mutating])

  const toggleRow = (stockCode) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(stockCode)) next.delete(stockCode)
      else next.add(stockCode)
      return next
    })
  }

  const toggleAllOnPage = () => {
    setSelected((prev) => {
      const next = new Set(prev)
      const allOnPage = (products.rows || []).map((r) => r.stockCode)
      const everyOneSelected = allOnPage.every((c) => next.has(c))
      if (everyOneSelected) {
        allOnPage.forEach((c) => next.delete(c))
      } else {
        allOnPage.forEach((c) => next.add(c))
      }
      return next
    })
  }

  const bulk = (exclude) => {
    if (selected.size === 0) return
    dispatch(setLicenseProductsExcluded(Array.from(selected), exclude))
  }

  const singleToggle = (row) => {
    if (!isAdmin) return
    dispatch(setLicenseProductsExcluded([row.stockCode], !row.isExcludedFromLicences))
  }

  const totalPages = Math.max(1, Math.ceil((products.totalCount || 0) / PAGE_SIZE))
  const onPage = (delta) => {
    const next = Math.min(Math.max(1, page + delta), totalPages)
    setPage(next)
    load(next)
  }

  return (
    <PageContainer>
      <Container fluid className="mt-3 mb-4">
        <Row>
          <Col>
            <Link to="/CSPLicenses" className="small">&larr; Back to 365 Licenses</Link>
            <PageHeading>Products</PageHeading>
            <small className="text-muted">
              Disable a product in the 365 Licences app. Engineers see disabled
              products in a collapsed section on the per-tenant detail page
              with a prompt to raise a Sales Admin ticket. Independent from
              the CSP Renewals exclusion list.
              {!isAdmin && ' (Read-only — only Admin can change this.)'}
            </small>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={4}>
            <div className="small text-muted mb-1">Search</div>
            <Input type="text" bsSize="sm" placeholder="SKU, name, or description"
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </Col>
          <Col md={3}>
            <div className="small text-muted mb-1">Show</div>
            <Input type="select" bsSize="sm" value={status}
              onChange={(e) => setStatus(e.target.value)}>
              <option value="all">All products</option>
              <option value="excluded">Excluded only</option>
              <option value="included">Included only</option>
            </Input>
          </Col>
          <Col md={3}>
            <div className="small text-muted mb-1">Pricing</div>
            <Input type="select" bsSize="sm" value={pricing}
              onChange={(e) => setPricing(e.target.value)}>
              <option value="all">All pricing</option>
              <option value="free">Free ($0) only</option>
              <option value="paid">Paid only</option>
            </Input>
          </Col>
          <Col md={2} className="d-flex align-items-end">
            <Button color="primary" size="sm" onClick={() => { setPage(1); load(1) }}>
              Apply
            </Button>
          </Col>
        </Row>

        {isAdmin && selected.size > 0 && (
          <Row className="mt-2">
            <Col>
              <div className="alert alert-info py-2 d-flex align-items-center">
                <strong className="mr-3">{selected.size} selected</strong>
                <Button size="sm" color="danger" className="mr-2"
                  disabled={mutating}
                  onClick={() => bulk(true)}>
                  Disable {selected.size} in 365 Licences
                </Button>
                <Button size="sm" color="success" className="mr-2"
                  disabled={mutating}
                  onClick={() => bulk(false)}>
                  Enable {selected.size} in 365 Licences
                </Button>
                <Button size="sm" color="link"
                  onClick={() => setSelected(new Set())}>
                  Clear selection
                </Button>
              </div>
            </Col>
          </Row>
        )}

        <Row className="mt-3">
          <Col>
            {loading ? (
              <LoadingSpinner />
            ) : (products.rows || []).length === 0 ? (
              <div className="text-muted">
                No products match. If you've just deployed, run a Dicker Data
                sync from the CSP Renewals page to populate the product list.
              </div>
            ) : (
              <>
                <div className="small text-muted mb-2">
                  {products.totalCount || 0} product(s) — page {page} of {totalPages}
                </div>
                <table className="table table-hover table-sm">
                  <thead>
                    <tr>
                      {isAdmin && (
                        <th style={{ width: 30 }}>
                          <Input type="checkbox" className="m-0"
                            style={{ position: 'relative' }}
                            checked={
                              (products.rows || []).length > 0
                              && (products.rows || []).every((r) => selected.has(r.stockCode))
                            }
                            onChange={toggleAllOnPage}
                          />
                        </th>
                      )}
                      <th>Stock code</th>
                      <th>Product</th>
                      <th className="text-right">Active subs</th>
                      <th className="text-right">Unit price</th>
                      <th className="text-right">RRP</th>
                      <th>Disabled in 365 Licences</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(products.rows || []).map((r) => (
                      <tr key={r.stockCode}>
                        {isAdmin && (
                          <td>
                            <Input type="checkbox" className="m-0"
                              style={{ position: 'relative' }}
                              checked={selected.has(r.stockCode)}
                              onChange={() => toggleRow(r.stockCode)}
                            />
                          </td>
                        )}
                        <td><code className="small">{r.stockCode}</code></td>
                        <td>
                          <div>{r.displayName}</div>
                          {r.stockDescription && r.stockDescription !== r.displayName && (
                            <div className="small text-muted">{r.stockDescription}</div>
                          )}
                        </td>
                        <td className="text-right">
                          <Badge color={r.activeSubscriptionCount > 0 ? 'primary' : 'secondary'}>
                            {r.activeSubscriptionCount}
                          </Badge>
                        </td>
                        <td className="text-right small">
                          {formatPriceRange(r.minUnitPrice, r.maxUnitPrice)}
                        </td>
                        <td className="text-right small">
                          {formatPriceRange(r.minRrp, r.maxRrp)}
                        </td>
                        <td>
                          {isAdmin ? (
                            <Button
                              size="sm"
                              color={r.isExcludedFromLicences ? 'danger' : 'outline-secondary'}
                              disabled={mutating}
                              onClick={() => singleToggle(r)}
                              title={r.isExcludedFromLicences && r.excludedByUpn
                                ? `Disabled by ${r.excludedByUpn} at ${formatLocal(r.excludedAt)}`
                                : undefined}
                            >
                              {r.isExcludedFromLicences ? 'Disabled' : 'Enabled'}
                            </Button>
                          ) : (
                            <Badge color={r.isExcludedFromLicences ? 'danger' : 'secondary'}>
                              {r.isExcludedFromLicences ? 'Disabled' : 'Enabled'}
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {totalPages > 1 && (
                  <div className="d-flex justify-content-end align-items-center">
                    <Button size="sm" color="outline-secondary" className="mr-2"
                      disabled={page <= 1} onClick={() => onPage(-1)}>← Prev</Button>
                    <Button size="sm" color="outline-secondary"
                      disabled={page >= totalPages} onClick={() => onPage(1)}>Next →</Button>
                  </div>
                )}
              </>
            )}
          </Col>
        </Row>
      </Container>
      <CSPLicenseDevPanel />
    </PageContainer>
  )
}

const CSPLicenseProducts = () => (
  <AzureAD provider={authProvider} reduxStore={store}>
    {({ accountInfo }) => {
      const realRoles = accountInfo?.account?.idToken?.roles || []
      const allowed = canUseLicenceFeature(realRoles)
      if (!allowed) {
        return (
          <PageContainer>
            <Container fluid className="mt-3">
              <p>You do not have permission to view this page.</p>
            </Container>
          </PageContainer>
        )
      }
      const isAdmin = realRoles.includes('Admin')
      return <CSPLicenseProductsInner isAdmin={isAdmin} />
    }}
  </AzureAD>
)

export default CSPLicenseProducts
